import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { formatApiError } from "../lib/api";
import { NumberChallenge } from "../components/NumberChallenge";
import { ShieldCheck, Home, KeyRound } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "tenant" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [human, setHuman] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) { setError("Please accept the Terms & Conditions to continue."); return; }
    if (!human) { setError("Please enter the confirmation number to prove you're human."); return; }
    setLoading(true);
    try {
      const data = await register(form);
      navigate(from || (data.user.role === "landlord" ? "/landlord" : "/tenant"));
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <div className="max-w-md mx-auto px-5 py-16">
        <div className="bg-mint-soft border border-mint/30 rounded-xl px-4 py-3 mb-6 flex items-center justify-between gap-2 text-sm">
          <span className="text-navy font-medium">Already have an account?</span>
          <Link to="/login" state={{ from }} data-testid="register-login-link" className="font-semibold text-mint-dark hover:underline">Log in here</Link>
        </div>
        <div className="flex items-center gap-2 mb-6">
          <div className="w-9 h-9 bg-mint flex items-center justify-center rounded-md"><ShieldCheck className="w-5 h-5 text-white" /></div>
          <h1 className="text-2xl font-bold font-heading text-navy">Create your account</h1>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {[
            { v: "tenant", icon: Home, t: "I'm a tenant", d: "Write verified reviews" },
            { v: "landlord", icon: KeyRound, t: "I'm a landlord", d: "Claim & respond" },
          ].map((r) => (
            <button key={r.v} type="button" data-testid={`role-${r.v}`} onClick={() => setForm({ ...form, role: r.v })}
              className={`text-left p-4 rounded-md border-2 transition-colors ${form.role === r.v ? "border-brand bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
              <r.icon className={`w-5 h-5 ${form.role === r.v ? "text-brand" : "text-slate-400"}`} />
              <p className="mt-2 font-semibold text-sm text-slate-900">{r.t}</p>
              <p className="text-xs text-slate-500">{r.d}</p>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="flex flex-col gap-4" data-testid="register-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-slate-700">First name</label>
              <input data-testid="register-firstname" required value={form.first_name} onChange={set("first_name")}
                className="mt-1 w-full border border-slate-300 rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-brand" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Last name</label>
              <input data-testid="register-lastname" required value={form.last_name} onChange={set("last_name")}
                className="mt-1 w-full border border-slate-300 rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-brand" />
            </div>
          </div>
          <p className="-mt-2 text-xs text-slate-500" data-testid="name-privacy-note">
            Your reviews will only ever show your first name and the initial of your surname (e.g. “John S.”). We keep your full name private and only use it to verify your tenancy.
          </p>
          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <input data-testid="register-email" type="email" required value={form.email} onChange={set("email")}
              className="mt-1 w-full border border-slate-300 rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-brand" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Password</label>
            <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")}
              className="mt-1 w-full border border-slate-300 rounded-md px-4 py-3 outline-none focus:ring-2 focus:ring-brand" />
          </div>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <input data-testid="register-agree" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#10B981]" />
            <span>I agree to the <Link to="/terms" className="text-mint-dark font-semibold underline">Terms &amp; Conditions</Link> and confirm any reviews I post will be truthful and based on my own experience.</span>
          </label>
          <NumberChallenge onValidChange={setHuman} /><button data-testid="register-submit" disabled={loading}
            className="bg-brand text-white font-semibold py-3 rounded-md hover:bg-brand-hover transition-colors disabled:opacity-60">
            {loading ? "Creating…" : "Create account"}
          </button>
        </form>
        <p className="mt-5 text-sm text-slate-500">Already have an account? <Link to="/login" className="text-brand font-medium">Log in</Link></p>
      </div>
    </div>
  );
}
